import { useCallback, useState } from "react";
import type { GridController } from "../focus/gridController";

/**
 * Marks which edges have content scrolled under them, so pinned columns draw
 * their edge border only then. Writes data attributes; never renders.
 */
export const updateOverflowEdges = (viewport: HTMLElement) => {
    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
    viewport.toggleAttribute("data-overflow-start", viewport.scrollLeft > 0);
    viewport.toggleAttribute(
        "data-overflow-end",
        viewport.scrollLeft < maxScrollLeft - 1,
    );
};

const scheduledViewports = new WeakSet<HTMLElement>();

/**
 * `updateOverflowEdges` in the next frame, once however often it's asked.
 * Its reads force a layout; called mid-commit, that layout is thrown away by
 * the rest of the commit's DOM writes. In a frame callback it is the layout
 * the frame needs anyway, still before paint.
 */
export const scheduleOverflowEdges = (viewport: HTMLElement) => {
    if (scheduledViewports.has(viewport)) {
        return;
    }
    scheduledViewports.add(viewport);
    requestAnimationFrame(() => {
        scheduledViewports.delete(viewport);
        if (viewport.isConnected) {
            updateOverflowEdges(viewport);
        }
    });
};

/**
 * Ref callback for the scroll viewport: registers it with the controller,
 * publishes its content width for fill columns, and keeps the overflow edge
 * attributes current through one passive scroll listener that skips vertical
 * scrolls (`ColumnSizeVars` updates them when widths change). The element is also
 * returned as state: the body's virtualizer mounts before the viewport ref
 * attaches and needs a render that sees it.
 */
export const useViewportObservers = (controller: GridController) => {
    const [viewportElement, setViewportElement] =
        useState<HTMLDivElement | null>(null);
    const viewportRef = useCallback(
        (viewport: HTMLDivElement | null) => {
            controller.setViewport(viewport);
            setViewportElement(viewport);
            if (!viewport) {
                return undefined;
            }
            // Not read here: a scroll position read forces a layout mid-commit.
            let lastScrollLeft = 0;
            const handleScroll = () => {
                if (viewport.scrollLeft === lastScrollLeft) {
                    return;
                }
                lastScrollLeft = viewport.scrollLeft;
                updateOverflowEdges(viewport);
            };
            viewport.addEventListener("scroll", handleScroll, {
                passive: true,
            });
            const resizeObserver = new ResizeObserver((entries) => {
                const entry = entries[entries.length - 1];
                if (entry) {
                    controller.containerWidth.set(
                        Math.floor(entry.contentRect.width),
                    );
                }
                updateOverflowEdges(viewport);
            });
            resizeObserver.observe(viewport);
            scheduleOverflowEdges(viewport);
            return () => {
                viewport.removeEventListener("scroll", handleScroll);
                resizeObserver.disconnect();
                controller.setViewport(null);
            };
        },
        [controller],
    );
    return { viewportRef, viewportElement };
};
